import React, { FC, useEffect, useState } from 'react';
import { select } from '~/lib/select';
import { TrainingState, useTrainingStore } from '~/training/state';
import { languageName } from '~/lib/translations/languageName';
import ArrowOrderIcon from '~/assets/icons/arrow-order.svg';
import { resolveLocale } from '~/ui/locale-selector/LocaleSelector';
import { Overlay } from '~/ui/overlay/Overlay';
import * as S from './LargeLocaleSelector.styled';
import { TextLocalePage } from '~/training/settings/locales/TextLocalePage';
import { Flex } from '~/ui/flex/Flex';
import { SettingsPages, useSettingsStore } from '~/training/settings/Store';
import { getSupportedLanguageLabel } from '~/lib/translations/getSupportedLanguageLabel';

interface Props {
  active: true | false;
}

const selector = (s: TrainingState) =>
  select(
    s,
    'setTextLocale',
    'textLocale',
    'supportedLanguages',
    'languageAutoDetect'
  );

export const LargeLocaleSelector: FC<{
  locales: string[];
  reloadSubtitle?: boolean;
  isOnboarding?: boolean;
}> = ({ locales, isOnboarding }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { textLocale, setTextLocale, supportedLanguages, languageAutoDetect } =
    useTrainingStore(selector);
  const { open, setPage } = useSettingsStore();
  const [selectedLocale, setSelectedLocale] = useState(textLocale);

  useEffect(() => {
    setSelectedLocale(textLocale);
  }, [textLocale]);

  useEffect(() => {
    let resolved = resolveLocale(window.navigator.language, locales);

    if (
      textLocale &&
      window.navigator.language != textLocale &&
      resolved == 'en' &&
      languageAutoDetect
    ) {
      resolved = resolveLocale(textLocale, locales);
    }

    if (locales.includes(resolved) && languageAutoDetect) {
      if (resolved != textLocale) {
        setTextLocale(resolved, isOnboarding);
      }
    } else {
      if (textLocale) {
        setTextLocale(textLocale, isOnboarding);
      } else {
        setTextLocale('en', isOnboarding);
      }
    }
  }, []);

  return (
    <>
      <S.Select
        onClick={() => {
          setIsModalOpen(true);
          setPage(SettingsPages.TextLocale);
        }}
      >
        <S.TextBlock>
          {languageName(selectedLocale)}

          <span>
            {getSupportedLanguageLabel(
              selectedLocale,
              supportedLanguages[selectedLocale]
            )}
          </span>
        </S.TextBlock>

        <ArrowOrderIcon />
      </S.Select>

      <Overlay open={isModalOpen} opacity={0.1}>
        <Flex
          zIndex="dialogs"
          p={{ _: 'sm', sm: 'xl' }}
          minHeight="100%"
          justifyContent={{ _: 'stretch', sm: 'center' }}
          alignItems={{ _: 'flex-end', sm: 'center' }}
          overflow="hidden"
          aria-modal={open}
        >
          <S.ModalBlock>
            <TextLocalePage
              onClose={() => setIsModalOpen(false)}
              centerTitle
              selectedLocale={selectedLocale}
              setSelectedLocale={
                setSelectedLocale as unknown as (value: string) => void
              }
            />
          </S.ModalBlock>
        </Flex>
      </Overlay>
    </>
  );
};
